import { useEffect, useMemo, useState } from "react";
import { type Bucket } from "../../shared/types.ts";
import { api } from "../api.ts";
import { Banner, BucketPicker } from "../components.tsx";

/** Strips a leading list marker ("- ", "* ", "• ", "1. ", "2)") from a pasted line. */
function stripMarker(line: string): string {
  return line.trim().replace(/^(?:[-*•]|\d+[.)])\s+/, "");
}

/** One project per non-blank line, forgiving of pasted bullet/number markers. */
function parseTitles(raw: string): string[] {
  return raw
    .split(/\r\n|\r|\n/)
    .map(stripMarker)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

/**
 * Room-by-room quick capture: pick an area once, paste or type a pile of
 * project titles (one per line), create them all in one go. Built for
 * gardening walkthroughs on a laptop, so it leans on the keyboard rather
 * than touch — but stays usable on a phone since nothing here is desktop-only.
 */
export function BulkAdd() {
  const [area, setArea] = useState("");
  const [bucket, setBucket] = useState<Bucket>("someday");
  const [raw, setRaw] = useState("");
  const [areas, setAreas] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ area: string; count: number } | null>(null);

  useEffect(() => {
    api
      .areas()
      .then(setAreas)
      .catch(() => {
        // Autocomplete is a nicety, not load-bearing — a failed fetch just
        // means free-typing only.
      });
  }, []);

  const titles = useMemo(() => parseTitles(raw), [raw]);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (titles.length === 0 || busy) return;

    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const trimmedArea = area.trim();
      const created = await api.createProjectsBulk({ area: trimmedArea, bucket, titles });
      setSuccess({ area: trimmedArea, count: created.length });
      setRaw("");
      if (!areas.includes(trimmedArea) && trimmedArea) {
        setAreas((current) => [...current, trimmedArea].sort((a, b) => a.localeCompare(b)));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not add those projects");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="bulk-add" onSubmit={submit}>
      <h2>Bulk add</h2>
      <p className="muted small">
        Pick an area, then paste or type one project per line. Great for walking a room and dumping every
        little thing that needs doing.
      </p>

      {error && <Banner kind="error">{error}</Banner>}
      {success && (
        <Banner kind="info">
          Added {success.count} project{success.count === 1 ? "" : "s"}
          {success.area ? ` to ${success.area}` : ""}. Keep going, or check the list.
        </Banner>
      )}

      <label htmlFor="bulk-area">Area</label>
      <input
        id="bulk-area"
        list="bulk-area-options"
        value={area}
        onChange={(e) => setArea(e.target.value)}
        placeholder="Front yard"
        autoFocus
      />
      <datalist id="bulk-area-options">
        {areas.map((option) => (
          <option key={option} value={option} />
        ))}
      </datalist>

      <label>When?</label>
      <BucketPicker value={bucket} onChange={setBucket} legend="When" />

      <label htmlFor="bulk-titles">Projects (one per line)</label>
      <textarea
        id="bulk-titles"
        className="bulk-textarea"
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
        placeholder={"Minor weeding on fountain\nBox collapse\nClean chairs and circle"}
        enterKeyHint="done"
      />
      <p className="muted small count-line">
        {titles.length} project{titles.length === 1 ? "" : "s"}
      </p>

      <button type="submit" className="primary big" disabled={busy || titles.length === 0}>
        {busy ? "Adding…" : `Add ${titles.length || ""} project${titles.length === 1 ? "" : "s"}`.trim()}
      </button>

      <p className="muted small pad">
        Only typed for one thing at a time? <a href="#/add">Use quick add →</a>
      </p>
    </form>
  );
}
